import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Building2, UserPlus, ChevronLeft, ChevronRight, Phone, Send } from "lucide-react";
import api from "@/lib/api";
import { dialNumber } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CustomerFormDialog from "@/components/CustomerFormDialog";

const PAGE_SIZE = 50;
const statusColor = {
  "Active Customer": "bg-success/15 text-success border-success/30",
  "No Sale": "bg-muted text-muted-foreground border-border",
};

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [outcome, setOutcome] = useState("");
  const [subFrom, setSubFrom] = useState("");
  const [subTo, setSubTo] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sel, setSel] = useState({});
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const navigate = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("search", q);
    if (outcome) params.set("outcome", outcome);
    if (subFrom) params.set("sub_start_from", subFrom);
    if (subTo) params.set("sub_start_to", subTo);
    params.set("page", page);
    params.set("page_size", PAGE_SIZE);
    return api.get(`/clients?${params.toString()}`)
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); setSel({}); })
      .catch(() => { setRows([]); setTotal(0); });
  };

  useEffect(() => { setPage(1); }, [q, outcome, subFrom, subTo]);
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q, outcome, subFrom, subTo, page]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("zeefa:refresh", h);
    return () => window.removeEventListener("zeefa:refresh", h);
  }, [q, outcome, subFrom, subTo, page]);
  useEffect(() => { api.get("/users").then((r) => setAgents(r.data.filter((u) => u.active))).catch(() => {}); }, []);

  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const selected = Object.keys(sel).filter((k) => sel[k]);
  const allChecked = rows.length > 0 && selected.length === rows.length;
  const toggleAll = () => setSel(allChecked ? {} : Object.fromEntries(rows.map((r) => [r.id, true])));

  const clearDates = () => { setSubFrom(""); setSubTo(""); };

  const assign = async () => {
    if (!agent) { toast.error("Choose an agent"); return; }
    if (selected.length === 0) { toast.error("Select customers"); return; }
    try {
      await api.post("/call-tasks", { customer_ids: selected, assigned_to: agent, reason: "Outbound" });
      toast.success(`Sent ${selected.length} to ${agent} for outbound calls`);
      setSel({});
    } catch (e) { toast.error("Could not assign — try again"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">{total.toLocaleString()} customer{total !== 1 && "s"} — search, filter by subscription start date, and assign for outbound calls.</p>
        </div>
        <Button className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="new-customer-page-btn"><UserPlus size={16} /> New Customer</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 h-10 px-3 rounded-sm border border-border bg-card flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="text-muted-foreground" />
          <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email…" className="w-full bg-transparent outline-none text-sm" />
        </div>
        <Tabs value={outcome || "all"} onValueChange={(v) => setOutcome(v === "all" ? "" : v)}>
          <TabsList className="rounded-sm">
            <TabsTrigger value="all" className="rounded-sm" data-testid="filter-all">All</TabsTrigger>
            <TabsTrigger value="Sale" className="rounded-sm" data-testid="filter-sale">Sales</TabsTrigger>
            <TabsTrigger value="No Sale" className="rounded-sm" data-testid="filter-nosale">No Sale</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="grid gap-1">
            <Label className="text-xs text-muted-foreground">Sub. start from</Label>
            <Input type="date" value={subFrom} onChange={(e) => setSubFrom(e.target.value)} className="rounded-sm w-40" data-testid="filter-sub-from" />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs text-muted-foreground">Sub. start to</Label>
            <Input type="date" value={subTo} onChange={(e) => setSubTo(e.target.value)} className="rounded-sm w-40" data-testid="filter-sub-to" />
          </div>
          {(subFrom || subTo) && <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" onClick={clearDates} data-testid="clear-sub-dates">Clear</Button>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="rounded-sm w-48" data-testid="assign-agent"><SelectValue placeholder="Assign to agent" /></SelectTrigger>
            <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.name}>{a.name} ({a.role})</SelectItem>)}</SelectContent>
          </Select>
          <Button className="rounded-sm gap-2" onClick={assign} data-testid="assign-btn"><Send size={15} /> Send ({selected.length})</Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all" /></TableHead>
              <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Plan</TableHead>
              <TableHead>Sub. Start</TableHead><TableHead>Outcome</TableHead><TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="clients-table-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No customers found.</TableCell></TableRow>}
            {rows.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={!!sel[c.id]} onCheckedChange={() => toggle(c.id)} data-testid={`sel-${c.id}`} /></TableCell>
                <TableCell className="font-medium flex items-center gap-2"><div className="h-7 w-7 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><Building2 size={13} /></div>{c.name}</TableCell>
                <TableCell className="font-mono text-sm" onClick={(e) => e.stopPropagation()}>
                  {c.phone ? <button onClick={() => dialNumber(c.phone)} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid={`dial-${c.id}`}><Phone size={13} /> {c.phone}</button> : "—"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.plan_name || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{c.subscription_start || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`rounded-sm ${statusColor[c.sales_status] || ""}`}>{c.outcome || "—"}</Badge></TableCell>
                <TableCell className="text-right font-mono">{c.amount ? "$" + Number(c.amount).toLocaleString() : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground" data-testid="clients-page-info">
          {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm gap-1" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="clients-prev-page"><ChevronLeft size={15} /> Prev</Button>
          <span className="text-sm px-2" data-testid="clients-page-count">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" className="rounded-sm gap-1" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="clients-next-page">Next <ChevronRight size={15} /></Button>
        </div>
      </div>

      <CustomerFormDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
